import React, { useState, useEffect, useReducer } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Button,
  Card,
  Container,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import {useDebounce} from 'react-use';
import { useAlert, TYPES } from 'hooks/use-alert-hook.js';
import BreadcrumbList from 'components/BreadcrumbList/BreadcrumbList.js';
import Question from './Widget/Question.js';
import QuestionContext from './Utils/context.js';
import QuestionReducer from './Utils/reducer.js';
import { ACTIONS } from './Utils/action.js';
import useNotif from 'hooks/use-notify-hook';
import natsort from 'hooks/natsort.js';

window.formula = [];

const QUESTION_TABLE_ID = 3;

export default function Edit() {

  const [swal, setSwal] = useAlert();
  const {instrumentId, criteriaId} = useParams();
  const [hasSchedules, setHasSchedules] = useState(true);
  const [notif, dispatchNotif] = useNotif();
  const [standard, setStandard] = useState();
  const [instrument, setInstrument] = useState();
  const [criteria, setCriteria] = useState();
  const [scores, setScores] = useState([]);
  const [questions, dispatchQuestions] = useReducer(QuestionReducer, []);
  const [sheets, setSheets] = useState([]);
  const [spreadsheet, setSpreadsheet] = useState(null);
  const [questionTypes, setQuestionTypes] = useState([]);

  const handleQuestionCreate = async () => {

    const payload = {
      INSTRUMENT_ID: instrumentId,
      CRITERIA_ID: criteriaId,
      QUESTION_LABEL: 'New Question',
      QUESTION_TYPE_ID: 1,
    };

    try {

      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST}questions`,
        payload,
        { headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('authenticated')).token}`,
          }
        }
      );

      dispatchQuestions({
        type: ACTIONS.CREATE,
        payload: response.data,
      });

    } catch (error) {
      setSwal({
        type: TYPES.ERROR,
        payload: {
          message: error.toString(),
        }
      });
    }
  }

  const [, cancelCriteria] = useDebounce(async () => {

    if (Object.keys(criteria).length === 0) return;

    const response = await axios.put(
      `${process.env.REACT_APP_API_HOST}criterias/${criteriaId}`,
      criteria,
      {headers: {Authorization: `Bearer ${JSON.parse(localStorage.getItem('authenticated')).token}`}}
    );

    dispatchNotif({
      place: `br`,
      message: `Update Success...`,
      type: `success`,
    });

  }, 2000, [criteria]);

  const fetchCriteria = async () => {

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}criterias/${criteriaId}`,
        {
          params: {expand: `questions,instrument.score.scoreIndices,instrument.standard,instrument.implementations`},
          headers: {Authorization: `Bearer ${JSON.parse(localStorage.getItem('authenticated')).token}`}
        },
      );

      cancelCriteria();

      const {
        questions,
        instrument: {
          score: { scoreIndices },
          standard,
        },
        instrument,
        ...rest
      } = response.data;

      setHasSchedules(instrument.implementations > 100);
      setStandard(standard);
      setInstrument(instrument);
      setCriteria(rest);
      setScores(scoreIndices);
      dispatchQuestions({
        type: ACTIONS.FETCH,
        payload: questions.sort((a, b) => a?.QUESTION_CODE && b?.QUESTION_CODE && natsort(a.QUESTION_CODE, b.QUESTION_CODE)),
      });

      cancelCriteria();

    } catch (error) {
      setSwal({
        type: TYPES.ERROR,
        payload: {
          message: error.toString(),
        }
      });
    }
  }

  const fetchQuestionType = async () => {

    try {

      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}question-types`,
        {
          params: { all: true },
          headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('authenticated')).token}`,
          },
        },
      );

      const questionTypes = instrument?.SHEET_FORMULA === undefined || instrument?.SHEET_FORMULA === null
        ? response.data.items.filter(question => question.QUESTION_TYPE_ID !== QUESTION_TABLE_ID)
        : response.data.items

      setQuestionTypes(questionTypes);

    } catch (error) {
      setSwal({
        type: TYPES.ERROR,
        payload: {
          message: error.toString(),
        }
      });
    }
  }

  const fetchSheets = async () => {

    if (!instrument?.SHEET_FORMULA) return ;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}instruments/${instrumentId}/spreadsheet`,
        {headers: {Authorization: `Bearer ${JSON.parse(localStorage.getItem('authenticated')).token}`}}
      );

      setSheets([...response.data].filter(sheet => sheet !== 'perhitungan'));

    } catch (error) {
      setSwal({
        type: TYPES.ERROR,
        payload: {
          message: error.toString(),
        }
      });
    }
  }


  useEffect(() => {
    cancelCriteria();
    fetchCriteria();
    cancelCriteria();
  }, []);

  useEffect(() => {
    if (instrument) {
      fetchSheets();
      fetchQuestionType();
    }
  }, [instrument])

  if (!criteria) return <p>Loading...</p>;

  return (
    <QuestionContext.Provider value={{
      questions,
      dispatchQuestions,
      spreadsheet,
      sheets,
      instrument,
    }}>
      {swal}{notif}
      <Container fluid>
        {standard && instrument && (
          <BreadcrumbList
            list={[
              { label: `Instruments`, to: `/admin/instruments` },
              { label: `${standard.STANDARD_NAME} ${instrument.INSTRUMENT_NAME}`, to: `/admin/instruments/${instrumentId}/edit`},
              { label: criteria.CRITERIA_LABEL }
            ]}
          />
        )}
        <Row>
          <Col md="11">
            <Row>
              <Col md="12">
                <Card>
                  <Card.Header>
                    <Card.Title as="h4">Criteria</Card.Title>
                  </Card.Header>
                  <Card.Body>

                    <Row className="align-items-center">
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Bullet</Form.Label>
                          <Form.Control
                            value={criteria.CRITERIA_CODE}
                            disabled={hasSchedules}
                            onChange={({target: {value}}) => {
                              setCriteria({...criteria, CRITERIA_CODE: value})
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group>
                          <Form.Label>Title</Form.Label>
                          <Form.Control
                            value={criteria.CRITERIA_LABEL}
                            disabled={hasSchedules}
                            onChange={({target: {value}}) => {
                              setCriteria({...criteria, CRITERIA_LABEL: value})
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group>
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        value={criteria.CRITERIA_DESCRIPTION || ``}
                        disabled={hasSchedules}
                        onChange={({target: {value}}) => {
                          setCriteria({...criteria, CRITERIA_DESCRIPTION: value})
                        }}
                        isInvalid={criteria?.CRITERIA_DESCRIPTION?.length > 4000}
                        rows={3}
                      />
                      <Form.Text id="passwordHelpBlock" muted>
                        Total characters: {criteria?.CRITERIA_DESCRIPTION?.length} / 4000
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {questions && questions.map((question, key) => (
              <Question key={key}
                scores={scores}
                index={key + 1}
                disabled={hasSchedules}
                questionTypes={questionTypes}
                {...question}
              />
            ))}
          </Col>
          {!hasSchedules && (
            <Col md="1">
              <Card style={{ position: 'fixed', }}>
                <Card.Body>
                  <Button onClick={handleQuestionCreate}><i className="fa fa-plus"></i></Button>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      </Container>
    </QuestionContext.Provider>
  );
}